if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const loadingSpinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');

        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            if (loadingSpinner) loadingSpinner.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
  function fetchDispatchData(variantId) {
    if (!variantId) return;
    $.ajax({
      url: "https://binary.zodiaconline.com/api/cdc-orders/sla-data",
      method: "POST",
      headers: {
        "X-API-KEY": "LP1uGTL14vD6zQzya1B4LPEvhReMgtwo"
      },
      contentType: "application/json",
      data: JSON.stringify({
        variant_id: parseInt(variantId)
      }),
      success: function(data) {
        if (data.success && data.message) {
          $("#delivery-message").html(`
            <span class="delivery-wrapper">
              <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M14.252 6.10352e-05C14.4348 6.10352e-05 14.6109 0.0729012 14.7402 0.202209C14.8693 0.331481 14.9414 0.506823 14.9414 0.689514V1.11432H16.457C16.8657 1.11545 17.2651 1.24122 17.6006 1.47467C17.9359 1.70803 18.1919 2.03834 18.335 2.42096L19.3359 5.13776C19.3769 5.2489 19.4434 5.34921 19.5303 5.42975L20.8037 6.61139C20.8754 6.67604 20.9334 6.75464 20.9727 6.84283C21.012 6.93111 21.0322 7.02745 21.0322 7.12408V11.7598C21.0322 11.9426 20.9593 12.1179 20.8301 12.2471C20.7008 12.3764 20.5256 12.4492 20.3428 12.4493H18.877C18.7583 12.9292 18.4821 13.3554 18.0928 13.6602C17.7035 13.9649 17.2238 14.1308 16.7295 14.1309C16.235 14.1309 15.7546 13.965 15.3652 13.6602C14.9759 13.3554 14.6997 12.9293 14.5811 12.4493H11.1133C10.9946 12.9292 10.7184 13.3554 10.3291 13.6602C9.93973 13.9651 9.45935 14.1309 8.96484 14.1309C8.47037 14.1309 7.98993 13.965 7.60059 13.6602C7.21138 13.3554 6.93506 12.9292 6.81641 12.4493H5.0752C5.00722 12.4493 4.93964 12.4355 4.87695 12.4092C4.81419 12.3829 4.75667 12.3445 4.70898 12.296C4.66129 12.2474 4.62381 12.1893 4.59863 12.126C4.57351 12.0628 4.56123 11.9948 4.5625 11.9268C4.56798 11.7907 4.62684 11.6622 4.72559 11.5684C4.82439 11.4747 4.95559 11.4233 5.0918 11.4249H6.80762C6.91926 10.9363 7.19411 10.5 7.58594 10.1876C7.97773 9.87521 8.46379 9.7052 8.96484 9.70514C9.46599 9.70514 9.95287 9.87515 10.3447 10.1876C10.7364 10.4999 11.0105 10.9365 11.1221 11.4249H14.5723C14.6839 10.9364 14.9579 10.5 15.3496 10.1876C15.7415 9.87515 16.2283 9.70514 16.7295 9.70514C17.2305 9.70521 17.7166 9.87522 18.1084 10.1876C18.5002 10.5 18.7741 10.9364 18.8857 11.4249H20.0078V7.27252L18.7705 6.15924H14.6064C14.4237 6.15921 14.2484 6.08721 14.1191 5.95807C13.9898 5.82876 13.917 5.65266 13.917 5.46979V1.02448H5.0752C5.00719 1.02447 4.93967 1.01072 4.87695 0.984436C4.81417 0.958081 4.75668 0.919754 4.70898 0.871155C4.66132 0.82257 4.6238 0.764473 4.59863 0.701233C4.57361 0.63819 4.56128 0.570808 4.5625 0.502991C4.56793 0.366792 4.62675 0.237479 4.72559 0.143616C4.82437 0.0499628 4.95567 -0.00152534 5.0918 6.10352e-05H14.252ZM8.7334 10.752C8.503 10.7978 8.29116 10.9112 8.125 11.0772C7.95888 11.2433 7.84571 11.4552 7.7998 11.6856C7.75397 11.916 7.77734 12.1551 7.86719 12.3721C7.95713 12.5893 8.10925 12.7757 8.30469 12.9063C8.50005 13.0368 8.72991 13.1065 8.96484 13.1065C9.27984 13.1062 9.58191 12.9806 9.80469 12.7579C10.0274 12.5351 10.1529 12.233 10.1533 11.918C10.1533 11.6831 10.0836 11.4533 9.95312 11.2579C9.82255 11.0624 9.63708 10.9094 9.41992 10.8194C9.20286 10.7295 8.96383 10.7062 8.7334 10.752ZM16.4971 10.752C16.2666 10.7979 16.0548 10.9111 15.8887 11.0772C15.7225 11.2433 15.6094 11.4552 15.5635 11.6856C15.5177 11.916 15.5411 12.1551 15.6309 12.3721C15.7208 12.5893 15.8739 12.7757 16.0693 12.9063C16.2647 13.0367 16.4946 13.1065 16.7295 13.1065C17.0444 13.1061 17.3466 12.9806 17.5693 12.7579C17.792 12.5351 17.9176 12.233 17.918 11.918C17.918 11.683 17.8474 11.4533 17.7168 11.2579C17.5862 11.0624 17.4008 10.9094 17.1836 10.8194C16.9665 10.7296 16.7275 10.7062 16.4971 10.752ZM5.45312 8.55865C5.52034 8.55868 5.58731 8.57199 5.64941 8.59772C5.71141 8.62346 5.76796 8.66158 5.81543 8.70905C5.86284 8.75655 5.90107 8.81305 5.92676 8.87506C5.95234 8.93696 5.96478 9.0034 5.96484 9.07037C5.96484 9.2061 5.9113 9.33662 5.81543 9.43268C5.71939 9.52872 5.58894 9.58301 5.45312 9.58307H1.8916C1.75658 9.58181 1.62728 9.52761 1.53223 9.4317C1.43726 9.33586 1.38391 9.20626 1.38379 9.07135C1.38379 8.93627 1.43715 8.80597 1.53223 8.71002C1.62728 8.61411 1.75658 8.55991 1.8916 8.55865H5.45312ZM8.69727 5.71198C8.83316 5.71198 8.96348 5.76628 9.05957 5.86237C9.15564 5.95845 9.20996 6.0888 9.20996 6.22467C9.20992 6.36051 9.15563 6.49092 9.05957 6.58698C8.96349 6.68301 8.83311 6.73737 8.69727 6.73737H0.517578C0.449908 6.73797 0.38203 6.72476 0.319336 6.69928C0.25673 6.6738 0.199376 6.63651 0.151367 6.58893C0.103402 6.54139 0.0660717 6.48425 0.0400391 6.42194C0.0140191 6.35951 1.76065e-05 6.2923 0 6.22467C0 6.157 0.014002 6.08987 0.0400391 6.0274C0.0660499 5.96506 0.103406 5.90798 0.151367 5.86041C0.199352 5.81286 0.256767 5.77554 0.319336 5.75006C0.38203 5.72458 0.449908 5.71137 0.517578 5.71198H8.69727ZM14.9414 5.13483H18.248L17.374 2.7774C17.3042 2.59057 17.1793 2.42947 17.0156 2.31549C16.8518 2.2015 16.6566 2.14026 16.457 2.13971H14.9414V5.13483ZM8.69727 2.86627C8.83304 2.86627 8.9635 2.91976 9.05957 3.01569C9.15555 3.11167 9.20984 3.24227 9.20996 3.37799C9.20996 3.51378 9.15553 3.64423 9.05957 3.7403C8.96348 3.83639 8.83316 3.89069 8.69727 3.89069H2.88672C2.75096 3.89059 2.62042 3.8363 2.52441 3.7403C2.42859 3.64425 2.375 3.51368 2.375 3.37799C2.37512 3.24227 2.42843 3.11167 2.52441 3.01569C2.62041 2.91971 2.75098 2.86637 2.88672 2.86627H8.69727Z" fill="#2D2D2D"/>
              </svg>  <span style="line-height: 100%;">${data.message}</span></span>`);
        }

      },
      error: function(error) {
        console.error("API Error:", error);
      }
    });
  }
  document.addEventListener('change', function(e) {
    if (e.target.name === "id") {
      fetchDispatchData(e.target.value);
    }
  });
  // Initial load
  let initialVariantId = $('[name="id"]').val();
  fetchDispatchData(initialVariantId);
  document.addEventListener('variant:change', function(event) {
    if (event.detail && event.detail.variant) {
      fetchDispatchData(event.detail.variant.id);
    }
  });
    let hasShownOnce = false; 

window.addEventListener('scroll', function () {
    const buyNow = document.querySelector('.pdp-buy-now');
    if (!buyNow) return;

    if (window.innerWidth <= 768) {
        if (window.scrollY > 180 || hasShownOnce) {
            buyNow.classList.add('mobile-sticky');
            hasShownOnce = true; 
        } else {
            buyNow.classList.remove('mobile-sticky');
        }
    } else {
        buyNow.classList.remove('mobile-sticky');
        hasShownOnce = false; 
    }
});

// fancybox

document.addEventListener("DOMContentLoaded", function () {

  // ✅ Build unique gallery manually
  function getGalleryItems() {
    const items = [];
    const seen = new Set();

    document.querySelectorAll('.product__media-item img').forEach((img) => {
      const src = img.src.replace(/width=\d+/, 'width=2000');

      if (!seen.has(src)) {
        seen.add(src);

        items.push({
          src: src,
          type: "image"
        });
      }
    });

    return items;
  }

  // ✅ Click handler
  document.addEventListener("click", function (e) {
    const trigger = e.target.closest(".fancybox-trigger");
    if (!trigger) return;

    e.preventDefault();

    const items = getGalleryItems();

    // find clicked index
    const clickedSrc = trigger.getAttribute("href");
    const startIndex = items.findIndex(item => item.src === clickedSrc);

    Fancybox.show(items, {
      startIndex: startIndex >= 0 ? startIndex : 0,

      Toolbar: {
        display: {
          middle: ["prev", "counter", "next"],
          right: ["zoom", "close"]
        }
      },

      Carousel: {
        Navigation: true
      },

      Images: {
        zoom: true
      },

      wheel: "zoom",
      caption: false,
      dragToClose: false
    });
  });

});

// magnifier lens
document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll(".magnifier-container").forEach(container => {
    const img = container.querySelector(".magnifier-image");
    const lens = container.querySelector(".magnifier-lens");

    const zoom = 2;

    function moveLens(e) {
      const rect = container.getBoundingClientRect();

      let x = e.clientX - rect.left;
      let y = e.clientY - rect.top;

      const lensSize = lens.offsetWidth / 2;

      let posX = x - lensSize;
      let posY = y - lensSize;

      // Boundaries
      posX = Math.max(0, Math.min(posX, container.offsetWidth - lens.offsetWidth));
      posY = Math.max(0, Math.min(posY, container.offsetHeight - lens.offsetHeight));

      lens.style.left = posX + "px";
      lens.style.top = posY + "px";

      lens.style.backgroundImage = `url(${img.src})`;
      lens.style.backgroundSize = `${img.offsetWidth * zoom}px ${img.offsetHeight * zoom}px`;
      lens.style.backgroundPosition = `-${x * zoom - lensSize}px -${y * zoom - lensSize}px`;
    }

    container.addEventListener("mousemove", moveLens);

    container.addEventListener("mouseenter", () => {
      lens.style.display = "block";
    });

    container.addEventListener("mouseleave", () => {
      lens.style.display = "none";
    });
  });
});


